import { useMemo, useRef, useState } from 'react'
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  type SensorDescriptor,
  type SensorOptions,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Check, GripVertical, LayoutIcon, Search } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export interface ColumnDef {
  key: string
  label: string
}

interface ColumnItemProps {
  column: ColumnDef
  isVisible: boolean
  onToggle: (key: string) => void
  draggable?: boolean
}

function ColumnItem({
  column,
  isVisible,
  onToggle,
  draggable = true,
}: ColumnItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.key,
    disabled: !draggable,
  })

  const style = draggable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 100 : undefined,
      }
    : undefined

  return (
    <div
      ref={draggable ? setNodeRef : undefined}
      style={style}
      className={`flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded-md transition-colors group ${
        isDragging ? 'bg-accent shadow-sm' : ''
      }`}
    >
      {draggable ? (
        <div
          {...attributes}
          {...listeners}
          className='cursor-grab active:cursor-grabbing p-0.5 text-muted-foreground/50 hover:text-muted-foreground transition-colors'
        >
          <GripVertical className='w-3.5 h-3.5' />
        </div>
      ) : (
        <div className='w-4' />
      )}
      <label className='flex flex-1 items-center gap-2 cursor-pointer'>
        <div
          className={`
            w-3.5 h-3.5 rounded border flex items-center justify-center transition-all duration-200
            ${
              isVisible
                ? 'bg-primary border-primary shadow-[0_0_0_1px_rgba(var(--primary),0.1)]'
                : 'bg-transparent border-muted-foreground/30 group-hover:border-primary/50'
            }
          `}
        >
          {isVisible && (
            <Check className='w-2.5 h-2.5 text-primary-foreground stroke-[3]' />
          )}
        </div>
        <input
          type='checkbox'
          className='sr-only'
          checked={isVisible}
          onChange={() => onToggle(column.key)}
        />
        <span
          className={`text-xs transition-colors ${
            isVisible
              ? 'text-foreground font-medium'
              : 'text-muted-foreground group-hover:text-foreground'
          }`}
        >
          {column.label}
        </span>
      </label>
    </div>
  )
}

interface ColumnVisibilityPopoverProps {
  columns: ColumnDef[]
  isColumnVisible: (key: string) => boolean
  onToggle: (key: string) => void
  onReset: () => void
  onDragEnd: (event: DragEndEvent) => void
  sensors: SensorDescriptor<SensorOptions>[]
  filterColumn?: (column: ColumnDef) => boolean
  showLabel?: boolean
  triggerClassName?: string
}

export function ColumnVisibilityPopover({
  columns,
  isColumnVisible,
  onToggle,
  onReset,
  onDragEnd,
  sensors,
  filterColumn,
  showLabel = false,
  triggerClassName,
}: ColumnVisibilityPopoverProps) {
  const [columnSearch, setColumnSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  const baseColumns = useMemo(
    () => (filterColumn ? columns.filter(filterColumn) : columns),
    [columns, filterColumn]
  )

  const filteredColumns = useMemo(() => {
    const query = columnSearch.trim().toLowerCase()
    if (!query) return baseColumns

    return baseColumns.filter(column =>
      column.label.toLowerCase().includes(query)
    )
  }, [baseColumns, columnSearch])

  const isSearching = columnSearch.trim().length > 0

  return (
    <Popover
      onOpenChange={open => {
        if (!open) setColumnSearch('')
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant='secondary'
          size='sm'
          className={
            triggerClassName ??
            'flex gap-2 h-9 text-xs border-border bg-card hover:!bg-transparent hover:text-foreground text-foreground transition-all rounded-lg px-3 w-full sm:w-auto justify-center'
          }
        >
          <LayoutIcon className='w-3.5 h-3.5' />
          {showLabel ? '' : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className='w-64 p-2'
        align='end'
        onOpenAutoFocus={event => {
          event.preventDefault()
          searchRef.current?.focus()
        }}
      >
        <div className='flex items-center justify-between px-2 py-1.5 border-b border-border mb-1'>
          <span className='text-[10px] font-bold text-muted-foreground uppercase tracking-wider'>
            Visible Columns
          </span>
          <Button
            variant='ghost'
            size='sm'
            onClick={onReset}
            className='h-auto p-0 text-[10px] text-primary hover:bg-transparent hover:text-primary/80 font-bold'
          >
            Reset
          </Button>
        </div>

        <div className='px-2 py-2'>
          <div className='relative'>
            <Search className='absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground' />
            <Input
              ref={searchRef}
              placeholder='Search columns...'
              value={columnSearch}
              onChange={event => setColumnSearch(event.target.value)}
              className='h-8 pl-8 text-xs border-border bg-card'
            />
          </div>
        </div>

        <div className='max-h-[350px] overflow-y-auto pr-1'>
          {filteredColumns.length === 0 ? (
            <p className='text-xs text-muted-foreground text-center py-4'>
              No columns found
            </p>
          ) : isSearching ? (
            filteredColumns.map(column => (
              <ColumnItem
                key={column.key}
                column={column}
                isVisible={isColumnVisible(column.key)}
                onToggle={onToggle}
                draggable={false}
              />
            ))
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={filteredColumns.map(column => column.key)}
                strategy={verticalListSortingStrategy}
              >
                {filteredColumns.map(column => (
                  <ColumnItem
                    key={column.key}
                    column={column}
                    isVisible={isColumnVisible(column.key)}
                    onToggle={onToggle}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
